import { types } from 'mobx-state-tree';
import createSubscription, { Unsubscribe, SubscriptionEventHandler, SubscriptionHandler } from './utils/createSubscription';
import getAppRoot from './utils/getAppRoot';
import UserPermissions from '../../permissions/constants/userPermissions';

// TODO: Refactor to be more DRY
const Subscription = types.model('Subscription')
  .props({
    projectSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    workspaceListingSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    routeSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    visitSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    driverSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    planningActionsSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    userSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    billingSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    userInviteSubscriptionOptions: types.frozen<SubscriptionHandler>(),
    myPendingInvitesSubscriptionOptions: types.frozen<SubscriptionHandler>(),
  })
  .actions((self) => {
    let prevDriverSubscription: Unsubscribe;
    let prevProjectSubscription: Unsubscribe;
    let prevWorkspaceListingSubscription: Unsubscribe;
    let prevRouteSubscription: Unsubscribe;
    let prevVisitSubscription: Unsubscribe;
    let prevSuggestionSubscription: Unsubscribe | undefined;
    let prevPlanningActionsSubscription: Unsubscribe;
    let prevUserSubscription: Unsubscribe;
    let prevBillingSubscription: Unsubscribe;
    let prevUserInviteSubscription: Unsubscribe;
    let prevMyPendingInvitesSubscription: Unsubscribe;

    return {
      startDriverSubscription(projectId?: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_DRIVERS)) {
          return () => { };
        }

        const variables = projectId ? { projectId } : {};

        prevDriverSubscription?.();
        prevDriverSubscription = createSubscription(
          getAppRoot(self).model.subscribeDriverSubscription, variables, self.driverSubscriptionOptions,
        );

        return prevDriverSubscription;
      },

      startProjectSubscription(projectId: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_PROJECTS)) {
          return () => { };
        }

        prevProjectSubscription?.();
        prevProjectSubscription = createSubscription(
          getAppRoot(self).model.subscribeProjectSubscription, { projectId }, self.projectSubscriptionOptions,
        );

        return prevProjectSubscription;
      },

      startWorkspaceListingSubscription(): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_PROJECTS)) {
          return () => { };
        }

        prevWorkspaceListingSubscription?.();
        prevWorkspaceListingSubscription = createSubscription(
          getAppRoot(self).model.subscribeWorkspaceListingSubscription, { }, self.workspaceListingSubscriptionOptions,
        );

        return prevWorkspaceListingSubscription;
      },

      startRouteSubscription(projectId: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_ROUTES)) {
          return () => { };
        }

        prevRouteSubscription?.();
        prevRouteSubscription = createSubscription(
          getAppRoot(self).model.subscribeRouteSubscription, { projectId }, self.routeSubscriptionOptions,
        );

        return prevRouteSubscription;
      },

      startVisitSubscription(projectId: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_VISITS)) {
          return () => { };
        }

        prevVisitSubscription?.();
        prevVisitSubscription = createSubscription(
          getAppRoot(self).model.subscribeVisitSubscription, { projectId }, self.visitSubscriptionOptions,
        );

        return prevVisitSubscription;
      },

      startPlanningActionsSubscription(projectId: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_PLANNING_ACTIONS)) {
          return () => {};
        }

        prevPlanningActionsSubscription?.();
        prevPlanningActionsSubscription = createSubscription(
          getAppRoot(self).model.subscribePlanningActionsSubscription, { projectId }, self.planningActionsSubscriptionOptions,
        );

        return prevPlanningActionsSubscription;
      },

      startUserSubscription(projectId?: string): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_TEAM_MEMBER_UPDATES)) {
          return () => { };
        }

        const variables = projectId ? { projectId } : {};

        prevUserSubscription?.();
        prevUserSubscription = createSubscription(
          getAppRoot(self).model.subscribeUserSubscription, variables, self.userSubscriptionOptions,
        );

        return prevUserSubscription;
      },

      startUserInviteSubscription(): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_TEAM_MEMBERS)) {
          return () => { };
        }

        const variables = {};

        prevUserInviteSubscription?.();
        prevUserInviteSubscription = createSubscription(
          getAppRoot(self).model.subscribeUserInviteSubscription, variables, self.userInviteSubscriptionOptions,
        );

        return prevUserInviteSubscription;
      },

      startMyPendingInvitesSubscription(): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_MY_USER)) {
          return () => { };
        }

        const variables = {};

        prevMyPendingInvitesSubscription?.();
        prevMyPendingInvitesSubscription = createSubscription(
          getAppRoot(self).model.subscribeMyPendingInvitesSubscription, variables, self.myPendingInvitesSubscriptionOptions,
        );

        return prevMyPendingInvitesSubscription;
      },

      startBillingSubscription(): Unsubscribe {
        if (!getAppRoot(self).account.hasPermission(UserPermissions.VIEW_BILLING)) {
          return () => { };
        }

        prevBillingSubscription?.();
        prevBillingSubscription = createSubscription(
          getAppRoot(self).model.subscribeBillingSubscription, {}, self.billingSubscriptionOptions,
        );

        return prevBillingSubscription;
      },

      stopSuggestionSubscription() {
        prevSuggestionSubscription?.();
      },

      stopDriverSubscription() {
        prevDriverSubscription?.();
      },

      stopProjectSubscriptions() {
        prevProjectSubscription?.();
        prevRouteSubscription?.();
        prevVisitSubscription?.();
        prevSuggestionSubscription?.();
        prevPlanningActionsSubscription?.();
        this.stopDriverSubscription();
      },

      stopWorkspaceListingSubscription() {
        prevWorkspaceListingSubscription?.();
      },

      stopUserSubscription() {
        prevUserSubscription?.();
      },

      stopUserInviteSubscription() {
        prevUserInviteSubscription?.();
      },

      stopMyPendingInvitesSubscription() {
        prevMyPendingInvitesSubscription?.();
      },

      stopBillingSubscription() {
        prevBillingSubscription?.();
      },

      onDriverEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.driverSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.driverSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.findIndex(handler);
            event.splice(idx, 1);
          }
        };
      },

      onProjectEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.projectSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.projectSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.indexOf(handler);
            event.splice(idx, 1);
          }
        };
      },

      onWorkspaceEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.workspaceListingSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.workspaceListingSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.indexOf(handler);
            event.splice(idx, 1);
          }
        };
      },

      onPlanningActionEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.planningActionsSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.planningActionsSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.indexOf(handler);
            event.splice(idx, 1);
          }
        };
      },

      onRouteEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.routeSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.routeSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.findIndex(handler);
            event.splice(idx, 1);
          }
        };
      },
      onVisitEvent(eventName: string, handler: SubscriptionEventHandler) {
        self.visitSubscriptionOptions.events.get(eventName)?.push(handler);

        return () => {
          const event = self.visitSubscriptionOptions.events.get(eventName);
          if (event != null) {
            const idx = event.findIndex(handler);
            event.splice(idx, 1);
          }
        };
      },
    };
  });

export default Subscription;
